/**
 * Selfie capture page with live camera liveness challenge.
 *
 * Provides three verification methods:
 * 1. **Camera mode (default)** -- Opens the device camera, runs a liveness
 *    challenge overlay, captures keyframes, then uploads the selfie and
 *    submits the challenge token for server-side verification.
 * 2. **Upload mode** -- A traditional file upload for users whose
 *    devices cannot access the camera. Uploads a selfie photo and starts a
 *    standard biometric face-match check (no liveness challenge).
 * 3. **Assisted verification** -- For accessibility needs, request a live
 *    video call with an agent for identity verification.
 *
 * WCAG 2.2 / EAA compliance:
 * - All three options are presented as equal-weight cards for discoverability
 * - Cards are keyboard-focusable `<button>` elements
 * - Container uses `role="group"` with descriptive `aria-label`
 * - Assisted verification provides an alternative path for users who cannot
 *   use camera or photo upload
 *
 * @module pages/SelfiePage
 */

import React, { useCallback, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { ApiClientError } from '@noxverify/api-client';
import type { ApplicantContext } from '../modules/context/context-loader';
import { getApiClient, uploadFileToSignedUrl } from '../lib/api';
import { StepIndicator } from '../components/StepIndicator';
import { FileUpload } from '../components/FileUpload';
import { CameraCapture } from '../components/CameraCapture';
import { LivenessChallenge } from '../components/LivenessChallenge';
import type { LivenessChallengeResult } from '../components/LivenessChallenge';
import { Footer } from '../components/Footer';

// ---------------------------------------------------------------------------
// Face oval illustration (shown on the instructions screen)
// ---------------------------------------------------------------------------

function FaceOvalIllustration() {
  const { t } = useTranslation('portal');
  return (
    <svg
      className="mx-auto h-40 w-32"
      viewBox="0 0 128 160"
      fill="none"
      aria-hidden="true"
    >
      {/* Outer guiding frame */}
      <rect
        x="4"
        y="4"
        width="120"
        height="152"
        rx="16"
        stroke="currentColor"
        strokeWidth="1"
        strokeDasharray="6 4"
        className="text-ink-muted"
      />
      {/* Face oval */}
      <ellipse
        cx="64"
        cy="72"
        rx="36"
        ry="46"
        stroke="currentColor"
        strokeWidth="2"
        strokeDasharray="4 3"
        className="text-[var(--nv-primary-color)]"
      />
      {/* Eyes */}
      <circle cx="50" cy="64" r="3" fill="currentColor" className="text-ink-muted" />
      <circle cx="78" cy="64" r="3" fill="currentColor" className="text-ink-muted" />
      {/* Nose */}
      <line x1="64" y1="68" x2="64" y2="78" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="text-ink-muted" />
      {/* Mouth */}
      <path
        d="M54 86 Q64 94 74 86"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
        className="text-ink-muted"
      />
      {/* Shoulder hints */}
      <path
        d="M28 130 Q64 118 100 130"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
        className="text-ink-muted"
      />
      {/* "Center your face" text area */}
      <text
        x="64"
        y="148"
        textAnchor="middle"
        className="text-ink-muted"
        fontSize="8"
        fill="currentColor"
      >
        {t('selfie.centerFace')}
      </text>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Tip items
// ---------------------------------------------------------------------------

interface TipItemProps {
  type: 'do' | 'dont';
  text: string;
}

function TipItem({ type, text }: TipItemProps) {
  const isDo = type === 'do';
  return (
    <li className="flex items-start gap-2.5">
      <span
        className={[
          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-white',
          isDo ? 'bg-primary' : 'bg-danger',
        ].join(' ')}
        aria-hidden="true"
      >
        {isDo ? (
          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        )}
      </span>
      <span className="text-sm text-ink-muted">{text}</span>
    </li>
  );
}

// ---------------------------------------------------------------------------
// SVG icons for the method selection cards
// ---------------------------------------------------------------------------

function CameraIcon() {
  return (
    <svg
      className="h-10 w-10"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M12 18.75H4.5a2.25 2.25 0 01-2.25-2.25V7.5A2.25 2.25 0 014.5 5.25h7.5"
      />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg
      className="h-10 w-10"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
      />
    </svg>
  );
}

function VideoCallIcon() {
  return (
    <svg
      className="h-10 w-10"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Status label helper
// ---------------------------------------------------------------------------

type PageStatus =
  | 'idle'
  | 'verifying'
  | 'uploading'
  | 'confirming'
  | 'submitting'
  | 'success'
  | 'error';

function getStatusLabel(status: PageStatus, t: TFunction): string {
  switch (status) {
    case 'verifying':
      return t('selfie.status.verifying');
    case 'uploading':
      return t('selfie.status.uploading');
    case 'confirming':
      return t('selfie.status.confirming');
    case 'submitting':
      return t('selfie.status.submitting');
    case 'success':
      return t('selfie.status.success');
    default:
      return t('selfie.status.default');
  }
}

// ---------------------------------------------------------------------------
// Assisted verification request form
// ---------------------------------------------------------------------------

interface AssistedFormData {
  name: string;
  email: string;
  preferredTime: string;
  accessibilityNeeds: string;
}

interface AssistedVerificationFormProps {
  caseId: string;
  onBack: () => void;
  onSubmitted: () => void;
}

function AssistedVerificationForm({
  caseId,
  onBack,
  onSubmitted,
}: AssistedVerificationFormProps) {
  const { t } = useTranslation('portal');
  const [form, setForm] = useState<AssistedFormData>({
    name: '',
    email: '',
    preferredTime: '',
    accessibilityNeeds: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setForm((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitting(true);
      setError(null);

      try {
        const client = getApiClient();
        await client.applicant.recordConsent(caseId, {
          consentType: 'biometric_collection',
        });
        onSubmitted();
      } catch (err: unknown) {
        setSubmitting(false);
        const apiErr = err as ApiClientError;
        setError(
          apiErr.message ??
            t('selfie.assisted.submitFailed'),
        );
      }
    },
    [caseId, onSubmitted, t],
  );

  const isValid =
    form.name.trim().length > 0 && form.email.trim().length > 0;

  return (
    <div className="rounded-lg border border-hairline bg-white p-6">
      <h3 className="mb-1 text-lg font-semibold text-ink">
        {t('selfie.assisted.title')}
      </h3>
      <p className="mb-6 text-sm text-ink-muted">
        {t('selfie.assisted.description')}
      </p>

      <form onSubmit={handleSubmit} noValidate>
        <div className="space-y-4">
          {/* Name */}
          <div>
            <label
              htmlFor="assisted-name"
              className="mb-1 block text-sm font-medium text-ink-muted"
            >
              {t('selfie.assisted.fullName')} <span aria-hidden="true">*</span>
              <span className="sr-only">{t('selfie.assisted.required')}</span>
            </label>
            <input
              id="assisted-name"
              name="name"
              type="text"
              required
              autoComplete="name"
              value={form.name}
              onChange={handleChange}
              disabled={submitting}
              className="block h-11 w-full rounded-md border border-hairline-strong bg-white px-3 text-sm text-ink transition-colors focus:border-[var(--nv-primary-color)] focus:outline-none focus:ring-2 focus:ring-[var(--nv-primary-color)]/20 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          {/* Email */}
          <div>
            <label
              htmlFor="assisted-email"
              className="mb-1 block text-sm font-medium text-ink-muted"
            >
              {t('selfie.assisted.email')} <span aria-hidden="true">*</span>
              <span className="sr-only">{t('selfie.assisted.required')}</span>
            </label>
            <input
              id="assisted-email"
              name="email"
              type="email"
              required
              autoComplete="email"
              value={form.email}
              onChange={handleChange}
              disabled={submitting}
              className="block h-11 w-full rounded-md border border-hairline-strong bg-white px-3 text-sm text-ink transition-colors focus:border-[var(--nv-primary-color)] focus:outline-none focus:ring-2 focus:ring-[var(--nv-primary-color)]/20 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          {/* Preferred time */}
          <div>
            <label
              htmlFor="assisted-time"
              className="mb-1 block text-sm font-medium text-ink-muted"
            >
              {t('selfie.assisted.preferredTime')}
            </label>
            <input
              id="assisted-time"
              name="preferredTime"
              type="text"
              autoComplete="off"
              placeholder={t('selfie.assisted.preferredTimePlaceholder')}
              value={form.preferredTime}
              onChange={handleChange}
              disabled={submitting}
              className="block h-11 w-full rounded-md border border-hairline-strong bg-white px-3 text-sm text-ink transition-colors placeholder:text-ink-muted focus:border-[var(--nv-primary-color)] focus:outline-none focus:ring-2 focus:ring-[var(--nv-primary-color)]/20 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          {/* Accessibility needs */}
          <div>
            <label
              htmlFor="assisted-needs"
              className="mb-1 block text-sm font-medium text-ink-muted"
            >
              {t('selfie.assisted.accessibilityNeeds')}
            </label>
            <textarea
              id="assisted-needs"
              name="accessibilityNeeds"
              rows={3}
              value={form.accessibilityNeeds}
              onChange={handleChange}
              disabled={submitting}
              placeholder={t('selfie.assisted.accessibilityPlaceholder')}
              className="block w-full rounded-md border border-hairline-strong bg-white px-3 py-2.5 text-sm text-ink transition-colors placeholder:text-ink-muted focus:border-[var(--nv-primary-color)] focus:outline-none focus:ring-2 focus:ring-[var(--nv-primary-color)]/20 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
        </div>

        {/* Error */}
        {error && (
          <div
            role="alert"
            className="mt-4 rounded-lg border border-danger/30 bg-danger-tint px-4 py-3 text-sm text-danger"
          >
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onBack}
            disabled={submitting}
            className="inline-flex h-11 items-center justify-center rounded-lg border border-hairline-strong px-6 text-sm font-medium text-ink-muted transition-all duration-200 hover:bg-surface-sunken focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            style={{ '--tw-ring-color': 'var(--nv-primary-color)' } as React.CSSProperties}
          >
            {t('selfie.assisted.back')}
          </button>
          <button
            type="submit"
            disabled={!isValid || submitting}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-md px-6 text-sm font-semibold text-white transition-all duration-200 hover:brightness-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              backgroundColor: 'var(--nv-primary-color)',
              '--tw-ring-color': 'var(--nv-primary-color)',
            } as React.CSSProperties}
          >
            {submitting && (
              <span
                className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
                aria-hidden="true"
              />
            )}
            {submitting ? t('selfie.assisted.submitting') : t('selfie.assisted.submit')}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Assisted verification confirmation screen
// ---------------------------------------------------------------------------

function AssistedVerificationConfirmation({
  onChooseAnother,
}: {
  onChooseAnother: () => void;
}) {
  const { t } = useTranslation('portal');
  return (
    <div className="rounded-lg border border-hairline bg-white p-8 text-center">
      {/* Success icon */}
      <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-primary-tint">
        <svg
          className="h-8 w-8 text-primary"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={3}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>

      <h3 className="mb-2 text-xl font-bold text-ink">
        {t('selfie.assisted.confirmationTitle')}
      </h3>
      <p className="mb-6 text-sm text-ink-muted">
        {t('selfie.assisted.confirmationDescription')}
      </p>

      <button
        type="button"
        onClick={onChooseAnother}
        className="text-sm font-medium underline transition-colors hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2"
        style={{
          color: 'var(--nv-primary-color)',
          '--tw-ring-color': 'var(--nv-primary-color)',
        } as React.CSSProperties}
      >
        {t('selfie.assisted.chooseDifferent')}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type CaptureMode = 'camera' | 'upload' | 'assisted';

interface SelfiePageProps {
  context: ApplicantContext;
}

export function SelfiePage({ context }: SelfiePageProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('portal');
  const { caseId } = useParams<{ caseId: string }>();
  const [searchParams] = useSearchParams();
  const submissionSetId = searchParams.get('ss');

  // -- Mode selection --
  const [mode, setMode] = useState<CaptureMode | null>(null);

  // -- Camera mode state --
  const [cameraStarted, setCameraStarted] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // -- Upload mode state --
  const [file, setFile] = useState<File | null>(null);

  // -- Assisted mode state --
  const [assistedSubmitted, setAssistedSubmitted] = useState(false);

  // -- Shared state --
  const [status, setStatus] = useState<PageStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const isBusy =
    status === 'verifying' ||
    status === 'uploading' ||
    status === 'confirming' ||
    status === 'submitting';

  // -------------------------------------------------------------------
  // Mode selection handlers
  // -------------------------------------------------------------------

  const selectCamera = useCallback(() => {
    setMode('camera');
    setFile(null);
    setStatus('idle');
    setError(null);
    setAssistedSubmitted(false);
  }, []);

  const selectUpload = useCallback(() => {
    setMode('upload');
    setCameraStarted(false);
    setStatus('idle');
    setError(null);
    setAssistedSubmitted(false);
  }, []);

  const selectAssisted = useCallback(() => {
    setMode('assisted');
    setCameraStarted(false);
    setFile(null);
    setStatus('idle');
    setError(null);
  }, []);

  const backToSelection = useCallback(() => {
    setMode(null);
    setCameraStarted(false);
    setFile(null);
    setStatus('idle');
    setError(null);
    setAssistedSubmitted(false);
  }, []);

  // -------------------------------------------------------------------
  // Camera mode: liveness challenge complete handler
  // -------------------------------------------------------------------

  const handleLivenessComplete = useCallback(
    async (result: LivenessChallengeResult) => {
      if (!caseId) return;

      setStatus('uploading');
      setError(null);

      let currentUploadId: string | null = null;

      try {
        const client = getApiClient();

        // Ensure a submission set exists.
        let ssId = submissionSetId;
        if (!ssId) {
          const ssResp = await client.applicant.createSubmissionSet(caseId);
          ssId = ssResp.data.id;
        }

        // Convert the selfie Blob to a File for the upload helper.
        const selfieFile = new File([result.selfieBlob], 'selfie.jpg', {
          type: 'image/jpeg',
        });

        // Step 1: Request an upload URL.
        const urlResp = await client.applicant.requestUploadUrl(ssId, {
          evidenceType: 'selfie',
          mimeType: selfieFile.type,
          fileName: selfieFile.name,
        });

        currentUploadId = urlResp.data.uploadId;

        // Step 2: PUT the selfie to the signed URL.
        await uploadFileToSignedUrl(urlResp.data.signedUrl, selfieFile);

        // Step 3: Confirm the upload.
        setStatus('confirming');
        await client.applicant.confirmUpload(currentUploadId, {
          fileSizeBytes: selfieFile.size,
        });

        // Step 4: Submit the liveness challenge.
        setStatus('submitting');
        await client.applicant.submitLivenessChallenge({
          caseId,
          challengeToken: result.challengeToken,
          keyframes: result.keyframes,
          frameHashChain: result.frameHashChain,
        });

        setStatus('success');
        navigate(`/case/${caseId}/confirmation`);
      } catch (err: unknown) {
        setStatus('error');
        const apiErr = err as ApiClientError;
        const supportRef = currentUploadId ? ` (ref: ${currentUploadId})` : '';
        setError(
          (apiErr.message ?? t('errors.unexpectedError')) +
            supportRef,
        );
      }
    },
    [caseId, submissionSetId, navigate, t],
  );

  // -------------------------------------------------------------------
  // Camera mode: liveness error handler
  // -------------------------------------------------------------------

  const handleLivenessError = useCallback((errorMsg: string) => {
    setStatus('error');
    setError(errorMsg);
    setCameraStarted(false);
  }, []);

  // -------------------------------------------------------------------
  // Upload mode: submit handler (existing flow)
  // -------------------------------------------------------------------

  const handleUploadSubmit = useCallback(async () => {
    if (!file || !caseId) return;

    setStatus('uploading');
    setError(null);

    let currentUploadId: string | null = null;

    try {
      const client = getApiClient();

      let ssId = submissionSetId;
      if (!ssId) {
        const ssResp = await client.applicant.createSubmissionSet(caseId);
        ssId = ssResp.data.id;
      }

      // Step 1: Request upload URL.
      const urlResp = await client.applicant.requestUploadUrl(ssId, {
        evidenceType: 'selfie',
        mimeType: file.type,
        fileName: file.name,
      });

      currentUploadId = urlResp.data.uploadId;

      // Step 2: PUT the file to the signed URL.
      await uploadFileToSignedUrl(urlResp.data.signedUrl, file);

      // Step 3: Confirm the upload.
      setStatus('confirming');
      await client.applicant.confirmUpload(currentUploadId, {
        fileSizeBytes: file.size,
      });

      // Step 4: Start a biometric check.
      setStatus('submitting');
      await client.applicant.startBiometricCheck({
        caseId,
        checkType: 'selfie_face_match',
      });

      setStatus('success');
      navigate(`/case/${caseId}/confirmation`);
    } catch (err: unknown) {
      setStatus('error');
      const apiErr = err as ApiClientError;
      const supportRef = currentUploadId ? ` (ref: ${currentUploadId})` : '';
      setError(
        (apiErr.message ?? t('errors.unexpectedError')) +
          supportRef,
      );
    }
  }, [file, caseId, submissionSetId, navigate, t]);

  // -------------------------------------------------------------------
  // No-op capture handler for CameraCapture (capture is driven by
  // LivenessChallenge, not the shutter button).
  // -------------------------------------------------------------------

  const handleCameraCapture = useCallback(() => {
    // Intentionally empty. The LivenessChallenge component captures frames
    // directly from the video ref rather than using the shutter button.
  }, []);

  // -------------------------------------------------------------------
  // Missing case ID guard
  // -------------------------------------------------------------------

  if (!caseId) {
    return (
      <div className="flex min-h-screen flex-col bg-surface">
        <main className="flex flex-1 items-center justify-center">
          <div className="rounded-lg border border-hairline bg-surface-raised p-8 text-center" role="alert">
            <h2 className="mb-2 text-xl font-bold text-ink">{t('app.missingCase')}</h2>
            <p className="mb-4 text-sm text-ink-muted">
              {t('app.missingCaseDescription')}
            </p>
            <Link
              to="/"
              className="inline-block rounded-lg px-6 py-2 text-sm font-medium text-white"
              style={{ backgroundColor: 'var(--nv-primary-color)' }}
            >
              {t('app.goToStart')}
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // -------------------------------------------------------------------
  // Page title per mode
  // -------------------------------------------------------------------

  let pageTitle = t('selfie.title.default');
  let pageDescription = t('selfie.description.default');

  if (mode === 'camera') {
    pageTitle = t('selfie.title.camera');
    pageDescription = t('selfie.description.camera');
  } else if (mode === 'upload') {
    pageTitle = t('selfie.title.upload');
    pageDescription = t('selfie.description.upload');
  } else if (mode === 'assisted') {
    pageTitle = t('selfie.title.assisted');
    pageDescription = t('selfie.description.assisted');
  }

  // -------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      {/* Header */}
      <header className="border-b border-hairline bg-surface">
        <div className="mx-auto flex max-w-2xl items-center gap-4 px-4 py-4">
          {context.branding.logoUrl && (
            <img
              src={context.branding.logoUrl}
              alt={t('app.logoAlt', { projectSlug: context.projectSlug })}
              className="h-8"
            />
          )}
          <h1
            className="text-lg font-semibold"
            style={{ color: 'var(--nv-primary-color)' }}
          >
            {t('selfie.pageHeading')}
          </h1>
        </div>
      </header>

      {/* Step indicator */}
      <div className="mx-auto w-full max-w-2xl px-4">
        <StepIndicator currentStep="selfie" />
      </div>

      <main className="flex-1">
        <div className="mx-auto max-w-2xl px-4 py-8">
          {/* Back button */}
          <button
            type="button"
            onClick={() => {
              if (mode !== null) {
                backToSelection();
              } else {
                navigate(-1);
              }
            }}
            disabled={isBusy}
            className="mb-6 inline-flex h-11 items-center gap-1.5 rounded-lg border border-hairline bg-white px-4 text-sm font-medium text-ink-muted transition-all duration-200 hover:bg-surface-sunken hover:text-ink focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            style={{ '--tw-ring-color': 'var(--nv-primary-color)' } as React.CSSProperties}
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            {t('app.back')}
          </button>

          {/* Page title */}
          <div className="mb-6 text-center">
            <h2 className="font-display text-3xl font-medium tracking-tight text-ink">{pageTitle}</h2>
            <p className="mt-1 text-sm text-ink-muted">{pageDescription}</p>
          </div>

          {/* ============================================================ */}
          {/* METHOD SELECTION CARDS                                       */}
          {/* ============================================================ */}
          {mode === null && (
            <div
              className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3"
              role="group"
              aria-label={t('selfie.chooseMethodAria')}
            >
              {/* Card 1: Camera */}
              <button
                type="button"
                onClick={selectCamera}
                className="nv-method-card group flex flex-col items-center rounded-lg border-2 border-hairline bg-white p-6 text-center transition-all duration-200 hover:border-[var(--nv-primary-color)] hover:focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{ '--tw-ring-color': 'var(--nv-primary-color)' } as React.CSSProperties}
              >
                <div
                  className="mb-4 flex h-16 w-16 items-center justify-center rounded-full transition-colors duration-200 group-hover:bg-[var(--nv-primary-color)] group-hover:text-white"
                  style={{ backgroundColor: 'color-mix(in srgb, var(--nv-primary-color) 10%, white)' }}
                >
                  <span style={{ color: 'var(--nv-primary-color)' }} className="group-hover:text-white">
                    <CameraIcon />
                  </span>
                </div>
                <h3 className="mb-1 text-base font-semibold text-ink">
                  {t('selfie.methodCards.camera.title')}
                </h3>
                <p className="text-sm text-ink-muted">
                  {t('selfie.methodCards.camera.description')}
                </p>
              </button>

              {/* Card 2: Upload */}
              <button
                type="button"
                onClick={selectUpload}
                className="nv-method-card group flex flex-col items-center rounded-lg border-2 border-hairline bg-white p-6 text-center transition-all duration-200 hover:border-[var(--nv-primary-color)] hover:focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{ '--tw-ring-color': 'var(--nv-primary-color)' } as React.CSSProperties}
              >
                <div
                  className="mb-4 flex h-16 w-16 items-center justify-center rounded-full transition-colors duration-200 group-hover:bg-[var(--nv-primary-color)] group-hover:text-white"
                  style={{ backgroundColor: 'color-mix(in srgb, var(--nv-primary-color) 10%, white)' }}
                >
                  <span style={{ color: 'var(--nv-primary-color)' }} className="group-hover:text-white">
                    <UploadIcon />
                  </span>
                </div>
                <h3 className="mb-1 text-base font-semibold text-ink">
                  {t('selfie.methodCards.upload.title')}
                </h3>
                <p className="text-sm text-ink-muted">
                  {t('selfie.methodCards.upload.description')}
                </p>
              </button>

              {/* Card 3: Assisted Verification */}
              <button
                type="button"
                onClick={selectAssisted}
                className="nv-method-card group flex flex-col items-center rounded-lg border-2 border-hairline bg-white p-6 text-center transition-all duration-200 hover:border-[var(--nv-primary-color)] hover:focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{ '--tw-ring-color': 'var(--nv-primary-color)' } as React.CSSProperties}
              >
                <div
                  className="mb-4 flex h-16 w-16 items-center justify-center rounded-full transition-colors duration-200 group-hover:bg-[var(--nv-primary-color)] group-hover:text-white"
                  style={{ backgroundColor: 'color-mix(in srgb, var(--nv-primary-color) 10%, white)' }}
                >
                  <span style={{ color: 'var(--nv-primary-color)' }} className="group-hover:text-white">
                    <VideoCallIcon />
                  </span>
                </div>
                <h3 className="mb-1 text-base font-semibold text-ink">
                  {t('selfie.methodCards.assisted.title')}
                </h3>
                <p className="text-sm text-ink-muted">
                  {t('selfie.methodCards.assisted.description')}
                </p>
              </button>
            </div>
          )}

          {/* ============================================================ */}
          {/* CAMERA MODE                                                  */}
          {/* ============================================================ */}
          {mode === 'camera' && !cameraStarted && (
            <>
              {/* Instructions card */}
              <div className="mb-6 rounded-lg border border-hairline bg-white p-6">
                <FaceOvalIllustration />

                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">
                      {t('selfie.tips.doHeading')}
                    </p>
                    <ul className="space-y-2">
                      <TipItem type="do" text={t('selfie.tips.do.lookAtCamera')} />
                      <TipItem type="do" text={t('selfie.tips.do.lighting')} />
                      <TipItem type="do" text={t('selfie.tips.do.centered')} />
                    </ul>
                  </div>
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-danger">
                      {t('selfie.tips.dontHeading')}
                    </p>
                    <ul className="space-y-2">
                      <TipItem type="dont" text={t('selfie.tips.dont.removeGlasses')} />
                      <TipItem type="dont" text={t('selfie.tips.dont.noFilters')} />
                      <TipItem type="dont" text={t('selfie.tips.dont.avoidBacklight')} />
                    </ul>
                  </div>
                </div>

                {/* Start verification button */}
                <div className="mt-6 flex justify-center">
                  <button
                    type="button"
                    onClick={() => {
                      setError(null);
                      setStatus('idle');
                      setCameraStarted(true);
                    }}
                    className="inline-flex h-11 items-center gap-2 rounded-md px-6 font-semibold text-white transition-all duration-200 hover:brightness-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface"
                    style={{
                      backgroundColor: 'var(--nv-primary-color)',
                      '--tw-ring-color': 'var(--nv-primary-color)',
                    } as React.CSSProperties}
                  >
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M12 18.75H4.5a2.25 2.25 0 01-2.25-2.25V7.5A2.25 2.25 0 014.5 5.25h7.5"
                      />
                    </svg>
                    {t('selfie.startVerification')}
                  </button>
                </div>
              </div>
            </>
          )}

          {mode === 'camera' && cameraStarted && (
            <div className="mb-6 rounded-lg border border-hairline bg-white p-4">
              {/* Status banner */}
              {isBusy && (
                <div
                  className="mb-4 flex items-center gap-2 rounded-lg bg-primary-tint px-4 py-3 text-sm text-primary-hover"
                  role="status"
                  aria-live="polite"
                >
                  <span
                    className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"
                    aria-hidden="true"
                  />
                  {getStatusLabel(status, t)}
                </div>
              )}

              <CameraCapture
                facingMode="user"
                onCapture={handleCameraCapture}
                showFaceOval
                videoRef={videoRef}
              >
                {/* LivenessChallenge overlay inside CameraCapture */}
                {!isBusy && status !== 'success' && (
                  <LivenessChallenge
                    caseId={caseId}
                    videoRef={videoRef}
                    onComplete={handleLivenessComplete}
                    onError={handleLivenessError}
                  />
                )}
              </CameraCapture>

              {/* Cancel / retry button below camera */}
              <div className="mt-4 flex justify-center">
                <button
                  type="button"
                  onClick={() => {
                    setCameraStarted(false);
                    setStatus('idle');
                    setError(null);
                  }}
                  disabled={isBusy}
                  className="text-sm font-medium text-ink-muted underline transition-colors hover:text-ink-muted focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  style={{ '--tw-ring-color': 'var(--nv-primary-color)' } as React.CSSProperties}
                >
                  {t('selfie.cancelGoBack')}
                </button>
              </div>
            </div>
          )}

          {/* ============================================================ */}
          {/* UPLOAD MODE                                                  */}
          {/* ============================================================ */}
          {mode === 'upload' && (
            <>
              {/* Instructions card (always visible in upload mode) */}
              <div className="mb-6 rounded-lg border border-hairline bg-white p-6">
                <FaceOvalIllustration />

                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">
                      {t('selfie.tips.doHeading')}
                    </p>
                    <ul className="space-y-2">
                      <TipItem type="do" text={t('selfie.tips.do.lookAtCamera')} />
                      <TipItem type="do" text={t('selfie.tips.do.lighting')} />
                      <TipItem type="do" text={t('selfie.tips.do.centered')} />
                    </ul>
                  </div>
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-danger">
                      {t('selfie.tips.dontHeading')}
                    </p>
                    <ul className="space-y-2">
                      <TipItem type="dont" text={t('selfie.tips.dont.removeGlasses')} />
                      <TipItem type="dont" text={t('selfie.tips.dont.noFilters')} />
                      <TipItem type="dont" text={t('selfie.tips.dont.avoidBacklight')} />
                    </ul>
                  </div>
                </div>
              </div>

              {/* Upload zone */}
              <div className="mb-6 rounded-lg border border-hairline bg-white p-6">
                <h3 className="mb-4 text-sm font-semibold text-ink">
                  {t('selfie.selfieRequired')} <span aria-hidden="true">*</span>
                  <span className="sr-only">{t('selfie.selfieRequiredAria')}</span>
                </h3>
                <FileUpload
                  id="selfie-upload"
                  label={t('selfie.selfieUploadLabel')}
                  accept="image/jpeg,image/png,image/webp"
                  onFileChange={setFile}
                  file={file}
                  disabled={isBusy}
                />
              </div>
            </>
          )}

          {/* ============================================================ */}
          {/* ASSISTED VERIFICATION MODE                                   */}
          {/* ============================================================ */}
          {mode === 'assisted' && !assistedSubmitted && (
            <AssistedVerificationForm
              caseId={caseId}
              onBack={backToSelection}
              onSubmitted={() => setAssistedSubmitted(true)}
            />
          )}

          {mode === 'assisted' && assistedSubmitted && (
            <AssistedVerificationConfirmation
              onChooseAnother={backToSelection}
            />
          )}

          {/* ============================================================ */}
          {/* Error display                                                */}
          {/* ============================================================ */}
          {error && (
            <div
              role="alert"
              className="mb-6 rounded-lg border border-danger/30 bg-danger-tint px-4 py-3 text-sm text-danger"
            >
              {error}
            </div>
          )}

          {/* ============================================================ */}
          {/* Mode toggle link                                             */}
          {/* ============================================================ */}
          {mode !== null && mode !== 'assisted' && (
            <div className="mb-6 text-center">
              <button
                type="button"
                onClick={backToSelection}
                disabled={isBusy}
                className="text-sm font-medium underline transition-colors hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                style={{
                  color: 'var(--nv-primary-color)',
                  '--tw-ring-color': 'var(--nv-primary-color)',
                } as React.CSSProperties}
              >
                {t('selfie.chooseDifferent')}
              </button>
            </div>
          )}

          {/* ============================================================ */}
          {/* Action area (submit button for upload mode)                  */}
          {/* ============================================================ */}
          <div className="rounded-lg border border-hairline bg-white p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-ink-muted">
                {t('selfie.step')}
              </p>

              {mode === 'upload' && (
                <button
                  type="button"
                  onClick={handleUploadSubmit}
                  disabled={!file || isBusy}
                  className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md px-6 font-semibold text-white transition-all duration-200 hover:brightness-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                  style={{
                    backgroundColor: 'var(--nv-primary-color)',
                    '--tw-ring-color': 'var(--nv-primary-color)',
                  } as React.CSSProperties}
                >
                  {isBusy && (
                    <span
                      className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
                      aria-hidden="true"
                    />
                  )}
                  {getStatusLabel(status, t)}
                </button>
              )}

              {mode === null && (
                <p className="text-sm italic text-ink-muted">
                  {t('selfie.actionHints.chooseMethod')}
                </p>
              )}

              {mode === 'camera' && !cameraStarted && (
                <p className="text-sm italic text-ink-muted">
                  {t('selfie.actionHints.startCamera')}
                </p>
              )}

              {mode === 'camera' && cameraStarted && !isBusy && (
                <p className="text-sm italic text-ink-muted">
                  {t('selfie.actionHints.followInstructions')}
                </p>
              )}

              {mode === 'camera' && isBusy && (
                <div className="flex items-center gap-2 text-sm text-ink-muted">
                  <span
                    className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-t-transparent"
                    style={{ borderColor: 'var(--nv-primary-color)', borderTopColor: 'transparent' }}
                    aria-hidden="true"
                  />
                  {getStatusLabel(status, t)}
                </div>
              )}

              {mode === 'assisted' && !assistedSubmitted && (
                <p className="text-sm italic text-ink-muted">
                  {t('selfie.actionHints.fillForm')}
                </p>
              )}

              {mode === 'assisted' && assistedSubmitted && (
                <p className="text-sm italic text-primary">
                  {t('selfie.actionHints.submitted')}
                </p>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer branding={context.branding} />
    </div>
  );
}
